/**
 * QuestionnaireLinksDialog — generazione e gestione dei link di invio di un
 * questionario.
 *
 * Due tipi di link: GENERICO (campagna: l'anagrafica arriva dal compilatore)
 * o PERSONALE (pre-agganciato a un'azienda del CRM). La generazione passa
 * dalla callable `createQuestionnaireLink` (token server-side); la lista e la
 * disabilitazione usano Firestore diretto (pattern console super_admin).
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, FormControlLabel, IconButton, Radio,
  RadioGroup, Stack, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Tooltip, Typography,
} from '@mui/material';
import {
  Block as BlockIcon, ContentCopy, Link as LinkIcon,
} from '@mui/icons-material';
import { Timestamp } from 'firebase/firestore';
import { normalizzaQuestionario } from '@shared/questionari';
import { tokens } from '../../theme';
import { SoftChip } from '../SoftChip';
import {
  buildQuestionnairePublicUrl, createQuestionnaireLink, disableQuestionnaireLink,
  distinctPartnerSegnalatori, listLinksForQuestionnaire, TOKEN_LEGGIBILE_RE,
} from '../../services/platformCrmService';
import type {
  PlatformLead, PlatformQuestionnaire, PlatformQuestionnaireLink,
} from '../../types/platformCrm';
import { formatDate } from './labels';

type TipoLink = 'generico' | 'personale';

interface Props {
  /** Questionario di cui gestire i link (null = dialog chiuso). */
  questionnaire: PlatformQuestionnaire | null;
  /** Aziende del CRM per la scelta del link personale. */
  leads: PlatformLead[];
  onClose: () => void;
  showSnack: (msg: string, severity: 'success' | 'error') => void;
}

export const QuestionnaireLinksDialog: React.FC<Props> = ({
  questionnaire, leads, onClose, showSnack,
}) => {
  const [tipo, setTipo] = useState<TipoLink>('generico');
  const [selectedLead, setSelectedLead] = useState<PlatformLead | null>(null);
  const [partnerSegnalatore, setPartnerSegnalatore] = useState('');
  const [scadenza, setScadenza] = useState('');
  const [nomeLeggibile, setNomeLeggibile] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generatedUrl, setGeneratedUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [links, setLinks] = useState<PlatformQuestionnaireLink[]>([]);
  const [linksLoading, setLinksLoading] = useState(false);

  const loadLinks = async (questionnaireId: string) => {
    setLinksLoading(true);
    try {
      setLinks(await listLinksForQuestionnaire(questionnaireId));
    } catch (e) {
      console.error('[platform-crm] caricamento link questionario fallito', e);
      showSnack('Errore nel caricamento dei link esistenti', 'error');
    } finally {
      setLinksLoading(false);
    }
  };

  // Reset + caricamento all'apertura. Dipende SOLO dal questionario: le
  // callback del padre cambiano identita' a ogni render e rilanciare il reset
  // azzererebbe il form (e l'URL appena generato) a ogni re-render del padre.
  useEffect(() => {
    if (!questionnaire) return;
    setTipo('generico');
    setSelectedLead(null);
    setPartnerSegnalatore('');
    setScadenza('');
    setNomeLeggibile('');
    setGeneratedUrl(null);
    setError(null);
    setLinks([]);
    loadLinks(questionnaire.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionnaire]);

  const handleGenerate = async () => {
    if (!questionnaire) return;
    if (tipo === 'personale' && !selectedLead) {
      setError('Seleziona l\'azienda a cui intestare il link');
      return;
    }
    const nome = nomeLeggibile.trim();
    if (tipo === 'generico' && nome && !TOKEN_LEGGIBILE_RE.test(nome)) {
      setError('Il nome leggibile va scritto con lettere minuscole, cifre e trattini, da 16 a 64 caratteri (es. lista-d-attesa-yumaos).');
      return;
    }
    setGenerating(true);
    setError(null);
    setGeneratedUrl(null);
    try {
      const result = await createQuestionnaireLink({
        questionnaire_id: questionnaire.id,
        platform_lead_id: tipo === 'personale' ? selectedLead?.id : undefined,
        // Partner di canale SOLO sui link campagna: i lead creati lo ereditano
        partner_segnalatore:
          tipo === 'generico' && partnerSegnalatore.trim() !== ''
            ? partnerSegnalatore.trim()
            : undefined,
        scadenza: scadenza ? new Date(`${scadenza}T23:59:59`).toISOString() : undefined,
        // Il nome leggibile vale solo per i link generici: un link personale resta un segreto.
        token_leggibile: tipo === 'generico' && nome ? nome : undefined,
      });
      setGeneratedUrl(result.url);
      showSnack('Link generato', 'success');
      await loadLinks(questionnaire.id);
    } catch (e) {
      const codice = (e as { code?: string })?.code ?? '';
      setError(
        codice.endsWith('already-exists')
          ? `Esiste già un link chiamato «${nome}»: scegli un altro nome.`
          : e instanceof Error ? e.message : 'Errore durante la generazione del link',
      );
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      showSnack('URL copiato negli appunti', 'success');
    } catch {
      showSnack('Copia non riuscita', 'error');
    }
  };

  const handleDisable = async (token: string) => {
    if (!questionnaire) return;
    try {
      await disableQuestionnaireLink(token);
      showSnack('Link disabilitato', 'success');
      await loadLinks(questionnaire.id);
    } catch (e) {
      showSnack(e instanceof Error ? e.message : 'Errore disabilitazione link', 'error');
    }
  };

  const leadNameById = new Map(leads.map((l) => [l.id, l.ragione_sociale]));

  // Partner già presenti nei lead: suggerimenti per coerenza dei nomi, senza hardcode
  const partnerOptions = useMemo(() => distinctPartnerSegnalatori(leads), [leads]);

  return (
    <Dialog open={!!questionnaire} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{
        display: 'flex', alignItems: 'center', gap: 1.5,
        fontFamily: tokens.font.display, fontSize: '1.5rem', fontWeight: 600, letterSpacing: '-0.02em',
      }}>
        <LinkIcon sx={{ color: tokens.color.tierPro.main }} />
        Link di invio — {questionnaire?.titolo}
      </DialogTitle>
      <DialogContent>
        {/* Generazione nuovo link */}
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
          Genera un nuovo link
        </Typography>
        <RadioGroup
          value={tipo}
          onChange={(e) => { setTipo(e.target.value as TipoLink); setError(null); }}
        >
          <FormControlLabel
            value="generico"
            control={<Radio size="small" />}
            label={(
              <Box>
                <Typography variant="body2" fontWeight={600}>Link generico (campagna)</Typography>
                <Typography variant="caption" color="text.secondary">
                  Riutilizzabile da più aziende
                  {questionnaire && normalizzaQuestionario(questionnaire).contatti
                    ? '; chi compila lascia i suoi contatti nell\'ultima schermata.'
                    : '. Attenzione: questo questionario non chiede i contatti, le risposte generiche saranno anonime.'}
                </Typography>
              </Box>
            )}
          />
          <FormControlLabel
            value="personale"
            control={<Radio size="small" />}
            label={(
              <Box>
                <Typography variant="body2" fontWeight={600}>Link personale</Typography>
                <Typography variant="caption" color="text.secondary">
                  Pre-agganciato a un'azienda del CRM: le risposte finiscono direttamente sul suo record.
                </Typography>
              </Box>
            )}
          />
        </RadioGroup>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 1.5 }}>
          {tipo === 'personale' && (
            <Autocomplete
              options={leads}
              value={selectedLead}
              onChange={(_, v) => { setSelectedLead(v); setError(null); }}
              getOptionLabel={(l) => `${l.ragione_sociale} — ${l.partita_iva || l.email || 'senza partita IVA'}`}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderInput={(params) => (
                <TextField {...params} label="Azienda" placeholder="cerca per nome o P.IVA" />
              )}
              sx={{ flex: 1, minWidth: 260 }}
              size="small"
            />
          )}
          {tipo === 'generico' && (
            <Autocomplete
              freeSolo
              options={partnerOptions}
              inputValue={partnerSegnalatore}
              onInputChange={(_, value) => setPartnerSegnalatore(value)}
              sx={{ flex: 1, minWidth: 260 }}
              size="small"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Partner segnalatore (opzionale)"
                  placeholder="es. Forniture Fotovoltaiche"
                  inputProps={{ ...params.inputProps, maxLength: 80 }}
                />
              )}
            />
          )}
          <TextField
            label="Scadenza (opzionale)"
            type="date"
            value={scadenza}
            onChange={(e) => setScadenza(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 190 }}
          />
          <Button
            variant="contained"
            onClick={handleGenerate}
            disabled={generating}
            startIcon={generating ? <CircularProgress size={16} /> : <LinkIcon />}
            sx={{ flexShrink: 0, alignSelf: { sm: 'center' } }}
          >
            {generating ? 'Generazione...' : 'Genera link'}
          </Button>
        </Stack>

        {tipo === 'generico' && (
          <TextField
            label="Nome leggibile del link (facoltativo)"
            value={nomeLeggibile}
            onChange={(e) => { setNomeLeggibile(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-')); setError(null); }}
            placeholder="lista-d-attesa-yumaos"
            fullWidth
            size="small"
            sx={{ mt: 1.5 }}
            inputProps={{ maxLength: 64, style: { fontFamily: tokens.font.mono, fontSize: '0.85rem' } }}
            helperText={
              nomeLeggibile.trim()
                ? `Diventa l'indirizzo: ${buildQuestionnairePublicUrl(nomeLeggibile.trim())}`
                : 'Diventa l\'indirizzo al posto di un codice casuale: lettere minuscole, cifre e trattini, da 16 a 64 caratteri. Vuoto = codice casuale.'
            }
          />
        )}

        {generatedUrl && (
          <Alert severity="success" sx={{ mt: 2 }} icon={false}>
            <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
              Link pronto da condividere
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                value={generatedUrl}
                fullWidth
                size="small"
                InputProps={{
                  readOnly: true,
                  sx: { fontFamily: tokens.font.mono, fontSize: '0.78rem', bgcolor: tokens.color.paper },
                }}
              />
              <Tooltip title="Copia URL">
                <IconButton size="small" onClick={() => handleCopy(generatedUrl)}>
                  <ContentCopy fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Alert>
        )}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

        <Divider sx={{ my: 2.5 }} />

        {/* Link esistenti */}
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
          Link esistenti
        </Typography>
        {linksLoading ? (
          <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress size={22} /></Box>
        ) : links.length === 0 ? (
          <Typography variant="body2" sx={{ color: tokens.color.mist }}>
            Nessun link generato per questo questionario.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Token</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell align="right">Usi</TableCell>
                <TableCell>Stato</TableCell>
                <TableCell>Creato</TableCell>
                <TableCell align="right">Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {links.map((link) => {
                const scaduto = link.scadenza instanceof Timestamp
                  && link.scadenza.toMillis() < Date.now();
                return (
                  <TableRow key={link.token}>
                    <TableCell>
                      <Box component="span" sx={{
                        fontFamily: tokens.font.mono, fontSize: '0.75rem', color: tokens.color.ash,
                      }}>
                        {TOKEN_LEGGIBILE_RE.test(link.token) ? link.token : `${link.token.slice(0, 10)}…`}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {link.platform_lead_id ? (
                        <SoftChip
                          label={leadNameById.get(link.platform_lead_id) ?? 'Personale'}
                          tone="primary"
                        />
                      ) : (
                        <SoftChip label="Generico" tone="default" />
                      )}
                      {!link.platform_lead_id && link.partner_segnalatore && (
                        <Typography
                          variant="caption"
                          sx={{ display: 'block', color: tokens.color.mist, mt: 0.25 }}
                        >
                          via {link.partner_segnalatore}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Box component="span" sx={{ fontFamily: tokens.font.mono, fontSize: '0.8rem' }}>
                        {link.usato_count ?? 0}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {link.disabled ? (
                        <SoftChip label="Disabilitato" tone="error" />
                      ) : scaduto ? (
                        <SoftChip label="Scaduto" tone="warning" />
                      ) : (
                        <SoftChip label="Attivo" tone="success" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ color: tokens.color.mist }}>
                        {formatDate(link.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="Copia URL">
                          <IconButton
                            size="small"
                            onClick={() => handleCopy(buildQuestionnairePublicUrl(link.token))}
                          >
                            <ContentCopy fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {!link.disabled && (
                          <Tooltip title="Disabilita link">
                            <IconButton
                              size="small"
                              sx={{ color: tokens.color.danger }}
                              onClick={() => handleDisable(link.token)}
                            >
                              <BlockIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Chiudi</Button>
      </DialogActions>
    </Dialog>
  );
};
